/**
 * Turning a failed Hub request into something the DJI screens can show (ADN-2307). Kept free of Angular imports so
 * it can be unit tested here (Angular's HTTP module cannot be loaded by this repo's jest setup).
 *
 * A non-2xx response reaches the app as an HttpErrorResponse: `status` is the HTTP status and `error` is the Hub's
 * ResponseModel body ({ status, data, errors }). That shape is recognised by its fields, not by class.
 */

/** A failed DJI request, with the Hub's own message. */
export class DjiRequestError extends Error {
  constructor(
    message: string,
    readonly httpStatus: number | null,
    /** Set for a 409 "already linked" answer: the spray record the flight already has. */
    readonly existingSprayRecordId: string | null
  ) {
    super(message);
    this.name = 'DjiRequestError';
  }
}

interface HttpFailureLike {
  status: number;
  error: unknown;
}

function isHttpFailure(error: unknown): error is HttpFailureLike {
  return typeof error === 'object' && error !== null && typeof (error as HttpFailureLike).status === 'number' && 'error' in error;
}

/**
 * The Hub's message from a failed request (`errors[0]`), else the error's own message, else `fallback`. A 409 that
 * names a spray record in its data is flagged so the caller can open that record instead of failing.
 */
export function toDjiRequestError(error: unknown, fallback: string): DjiRequestError {
  if (error instanceof DjiRequestError) {
    return error;
  }

  if (isHttpFailure(error)) {
    const body = error.error as { errors?: unknown; data?: { sprayRecordId?: unknown } } | null;
    const first = Array.isArray(body?.errors) ? body.errors[0] : null;
    const message = typeof first === 'string' && first.trim() !== '' ? first : fallback;
    const existing = error.status === 409 && typeof body?.data?.sprayRecordId === 'string' ? body.data.sprayRecordId : null;
    return new DjiRequestError(message, error.status, existing);
  }

  if (error instanceof Error && error.message) {
    return new DjiRequestError(error.message, null, null);
  }
  return new DjiRequestError(fallback, null, null);
}
